/**
 * ContactModal Component
 * 
 * This component provides a messaging interface for contacting a VA.
 * It handles message composition, sending, and conversation management.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Paperclip, AlertCircle, Check } from 'lucide-react';
import { conversationService } from '../../services/conversationService';
import { useAuth } from '../../hooks/useAuth';

const ContactModal = ({ 
  vaId, 
  vaName, 
  conversationId, 
  jobId = null,
  recipient = null, // New prop for when passed as recipient object
  isOpen, 
  onClose, 
  onMessageSent 
}) => {
  const { user } = useAuth();
  const [messageText, setMessageText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSent, setIsSent] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);
  const textareaRef = useRef(null);

  // Extract recipient data from either individual props or recipient object
  const recipientId = vaId || recipient?.id;
  const recipientName = vaName || recipient?.name;
  const relatedJobId = jobId || recipient?.relatedTo?.jobId;

  const maxCharacters = 2000;

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Update character count
  useEffect(() => {
    setCharacterCount(messageText.length);
  }, [messageText]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim()) {
      setError('Please enter a message');
      return;
    }

    if (messageText.length > maxCharacters) {
      setError(`Message is too long. Maximum ${maxCharacters} characters allowed.`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Validate user is authenticated
      if (!user?.id) {
        throw new Error('User must be authenticated to send messages');
      }

      // Validate recipient
      if (!recipientId) {
        throw new Error('Recipient information is required');
      }
      
      // Create or get conversation if not provided
      let conversationToUse = currentConversationId;
      if (!conversationToUse) {
        const conversation = await conversationService.createOrGetConversation(
          user.id,
          recipientId,
          relatedJobId || null // Ensure we pass null instead of undefined
        );
        conversationToUse = conversation.id;
        setCurrentConversationId(conversation.id);
      }
      
      const message = await conversationService.sendMessage(
        conversationToUse,
        user.id,        // Use actual authenticated user ID
        recipientId,   // recipient ID
        messageText.trim(),
        'text',
        []
      );

      setIsSent(true);
      setMessageText('');
      
      // Call success callback
      if (onMessageSent) {
        onMessageSent(message);
      }

      // Show success state briefly then close
      setTimeout(() => {
        onClose();
        setIsSent(false);
      }, 1500);

    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSendMessage(e);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setMessageText('');
      setError(null);
      setIsSent(false);
      setCurrentConversationId(conversationId); // Reset to original conversationId
      onClose();
    }
  };

  if (!isOpen) return null;

  // Success state
  if (isSent) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Message sent!</h2>
            <p className="text-gray-600">Your message has been sent to {recipientName || 'the candidate'}.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Contact {recipientName || 'Candidate'}</h1>
              {(jobId || relatedJobId) && (
                <p className="text-sm text-gray-500 mt-1">Regarding job opportunity</p>
              )}
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSendMessage} className="p-6">
            <div className="space-y-4">
              {/* Message Input */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  ref={textareaRef}
                  id="message"
                  rows={6}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Write your message to ${recipientName || 'candidate'}...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors duration-200"
                  disabled={isLoading}
                  maxLength={maxCharacters}
                />
                
                {/* Character Count */}
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    Press Ctrl+Enter to send quickly
                  </p>
                  <span className={`text-xs ${
                    characterCount > maxCharacters * 0.9 
                      ? characterCount > maxCharacters ? 'text-red-600' : 'text-yellow-600'
                      : 'text-gray-500'
                  }`}>
                    {characterCount}/{maxCharacters}
                  </span>
                </div>
              </div>

              {/* Suggested Templates */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Quick Templates:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Hi! I'm interested in working with you on a project.",
                    "I'd like to discuss a potential collaboration.",
                    "Your skills look like a great fit for our needs."
                  ].map((template, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setMessageText(template)}
                      disabled={isLoading}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200 disabled:opacity-50"
                    >
                      {template}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Error sending message</p>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={isLoading || !messageText.trim() || messageText.length > maxCharacters}
                  className="inline-flex items-center gap-2 px-4 py-2 disabled:bg-gray-300 text-white rounded-lg font-medium transition-opacity duration-200 hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* Tips */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2" />
              <div className="text-xs text-gray-600">
                <p className="font-medium mb-1">Tips for better responses:</p>
                <ul className="space-y-1">
                  <li>• Be specific about your project needs</li>
                  <li>• Mention your timeline and budget if relevant</li>
                  <li>• Ask about their availability</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;

